/**
 * Zod validation schemas for Settings types
 * Aligned with API types from @kisanlink/erp-service
 */

import { z } from 'zod';

/**
 * Address Schema
 * Used for both branch and registered addresses
 */
export const addressSchema = z.object({
  type: z.string().min(1, 'Address type is required'),
  line1: z.string().min(1, 'Address line 1 is required'),
  line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  district: z.string().min(1, 'District is required'),
  state: z.string().min(1, 'State is required'),
  pincode: z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits'),
  country: z.string().min(1, 'Country is required').default('India'),
});

/**
 * Bank Details Schema
 */
export const bankDetailsSchema = z.object({
  bank_name: z.string().min(1, 'Bank name is required'),
  account_number: z.string().min(1, 'Account number is required'),
  ifsc_code: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'IFSC code must be in format: AAAA0XXXXXX'),
  branch_name: z.string().min(1, 'Branch name is required'),
  account_type: z.string().min(1, 'Account type is required'),
});

/**
 * GSTIN Schema
 */
export const gstinSchema = z.string()
  .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    'Invalid GSTIN format. Expected: 22AAAAA0000A1Z5');

/**
 * PAN Schema
 */
export const panSchema = z.string()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    'Invalid PAN format. Expected: AAAAA0000A');

/**
 * CIN Schema
 */
export const cinSchema = z.string()
  .regex(/^[UL][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
    'Invalid CIN format. Expected: U12345AA1234AAA123456');

/**
 * TAN Schema
 */
export const tanSchema = z.string()
  .regex(/^[A-Z]{4}[0-9]{5}[A-Z]{1}$/,
    'Invalid TAN format. Expected: AAAA00000A');

/**
 * Setting Response Schema
 * Matches SettingResponse from erp-service
 */
export const settingSchema = z.object({
  id: z.string(),
  key: z.string(),
  value: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

/**
 * Invoice Requirements Response Schema
 */
export const invoiceRequirementsSchema = z.object({
  ready: z.boolean(),
  missing_settings: z.array(z.string()),
});

/**
 * Header Field Response Schema
 */
export const headerFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
  display_order: z.number(),
});

// Type exports
export type Address = z.infer<typeof addressSchema>;
export type BankDetails = z.infer<typeof bankDetailsSchema>;
export type Setting = z.infer<typeof settingSchema>;
export type InvoiceRequirements = z.infer<typeof invoiceRequirementsSchema>;
export type HeaderField = z.infer<typeof headerFieldSchema>;

// Helper functions for JSON string conversion
/**
 * Parse address from JSON string
 */
export const parseAddress = (jsonString: string | null | undefined): Address | null => {
  if (!jsonString) return null;
  try {
    const parsed = JSON.parse(jsonString);
    return addressSchema.parse(parsed);
  } catch {
    return null;
  }
};

/**
 * Parse bank details from JSON string
 */
export const parseBankDetails = (jsonString: string | null | undefined): BankDetails | null => {
  if (!jsonString) return null;
  try {
    const parsed = JSON.parse(jsonString);
    return bankDetailsSchema.parse(parsed);
  } catch {
    return null;
  }
};

/**
 * Stringify address to JSON string
 */
export const stringifyAddress = (address: Address): string => {
  return JSON.stringify(address);
};

/**
 * Stringify bank details to JSON string
 */
export const stringifyBankDetails = (bankDetails: BankDetails): string => {
  return JSON.stringify(bankDetails);
};



